import { create } from 'zustand';
import type { Claim, CouncilInsight, Document, PriorReport } from '@fixtyer/shared';
import { api } from '@/lib/api';

interface PriorReportsSearchResult {
  found: number;
  totalNearby: number;
  reports: (PriorReport & { daysBefore: number })[];
  searchLocation: {
    lat: number;
    lng: number;
    address: string | null;
  };
  reportUrl: string | null;
}

interface AuthorityInfo {
  id?: string;
  name: string;
  councilName?: string;
  type: string;
  region?: string;
  contactEmail?: string;
  contactAddress?: string;
  claimsPortalUrl?: string;
  phone?: string;
  typicalResponseDays?: number;
  historicalSuccessRate?: number;
  roadType?: string;
  adminDistrict?: string;
  adminCounty?: string;
}

interface AuthorityResult {
  authority: AuthorityInfo | null;
  suggestion?: AuthorityInfo;
  confidence?: 'high' | 'medium' | 'low';
  roadType?: string;
  message?: string;
  councilInsight?: CouncilInsight | null;
}

interface ClaimPack {
  id: string;
  claimId: string;
  packType: string;
  version: number;
  fileUrl: string;
  generatedAt: string;
}

interface ClaimsState {
  claims: Claim[];
  currentClaim: Claim | null;
  documents: Document[];
  priorReports: (PriorReport & { daysBefore?: number })[];
  packs: ClaimPack[];
  isPackGenerating: boolean;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  fetchClaims: () => Promise<void>;
  fetchClaim: (id: string) => Promise<void>;
  createClaim: (data: Partial<Claim>) => Promise<Claim>;
  updateClaim: (id: string, data: Partial<Claim>) => Promise<void>;
  deleteClaim: (id: string) => Promise<void>;
  
  // Documents
  fetchDocuments: (claimId: string) => Promise<void>;
  uploadDocument: (claimId: string, file: File, type: string) => Promise<Document>;
  deleteDocument: (documentId: string, options?: { reason?: string; category?: string }) => Promise<void>;
  pollForDocumentAnalysis: (claimId: string, documentId: string) => Promise<void>;
  
  // Analysis
  analyseEvidence: (claimId: string) => Promise<void>;
  
  // Prior Reports
  fetchPriorReports: (claimId: string) => Promise<void>;
  searchPriorReports: (claimId: string) => Promise<PriorReportsSearchResult>;
  confirmPriorReport: (claimId: string, reportId: string) => Promise<void>;
  removePriorReport: (claimId: string, reportId: string) => Promise<void>;
  
  // Authority
  identifyAuthority: (claimId: string) => Promise<AuthorityResult>;
  fetchAuthority: (claimId: string) => Promise<AuthorityResult>;
  
  // Packs
  fetchPacks: (claimId: string) => Promise<void>;
  setPackGenerating: (generating: boolean) => void;
  
  // Realtime
  getRealtimeToken: (claimId: string, runIds?: string[]) => Promise<{ publicToken: string; claimTag: string; expiresIn: number }>;
  
  clearCurrentClaim: () => void;
}

// Stale-response guards: claim-scoped responses are only applied if the claim
// they belong to is still the one being viewed, and long-running polls bail
// out when the generation changes (navigation away / different claim loaded).
let activeClaimId: string | null = null;
let pollGeneration = 0;

const isStale = (claimId: string) => activeClaimId !== claimId;

export const useClaimsStore = create<ClaimsState>((set, get) => ({
  claims: [],
  currentClaim: null,
  documents: [],
  priorReports: [],
  packs: [],
  isPackGenerating: false,
  isLoading: false,
  error: null,

  fetchClaims: async () => {
    set({ isLoading: true, error: null });
    try {
      const data = await api.get<{ claims: Claim[] }>('/api/claims');
      set({ claims: data.claims, isLoading: false });
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to fetch claims',
        isLoading: false 
      });
    }
  },

  fetchClaim: async (id: string) => {
    if (activeClaimId !== id) {
      // A different claim is being loaded: cancel any in-flight polls for the old one
      pollGeneration++;
    }
    activeClaimId = id;

    // Only show loading skeleton on initial load, not refreshes
    const hasExistingClaim = !!get().currentClaim;
    if (!hasExistingClaim) {
      set({ isLoading: true, error: null });
    }
    
    try {
      const data = await api.get<Claim>(`/api/claims/${id}`);
      if (isStale(id)) return;
      set({ currentClaim: data, isLoading: false });
      
      // Also fetch documents, prior reports, and packs (silently)
      get().fetchDocuments(id);
      get().fetchPriorReports(id);
      get().fetchPacks(id);
    } catch (error) {
      if (isStale(id)) return;
      set({ 
        error: error instanceof Error ? error.message : 'Failed to fetch claim',
        isLoading: false 
      });
    }
  },

  createClaim: async (data: Partial<Claim>) => {
    set({ isLoading: true, error: null });
    try {
      const claim = await api.post<Claim>('/api/claims', data);
      set((state) => ({ 
        claims: [claim, ...state.claims],
        currentClaim: claim,
        isLoading: false 
      }));
      return claim;
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to create claim',
        isLoading: false 
      });
      throw error;
    }
  },

  updateClaim: async (id: string, data: Partial<Claim>) => {
    // Optimistic update - update UI immediately
    const previousClaim = get().currentClaim;
    if (previousClaim?.id === id) {
      set((state) => ({
        currentClaim: { ...previousClaim, ...data } as Claim,
        claims: state.claims.map((c) => (c.id === id ? { ...c, ...data } : c)),
      }));
    }
    
    try {
      const updatedClaim = await api.patch<Claim>(`/api/claims/${id}`, data);
      
      // Sync with server response
      set((state) => ({
        claims: state.claims.map((c) => (c.id === id ? updatedClaim : c)),
        currentClaim: state.currentClaim?.id === id ? updatedClaim : state.currentClaim,
      }));
    } catch (error) {
      // Rollback on error
      if (previousClaim) {
        set((state) => ({
          currentClaim: previousClaim,
          claims: state.claims.map((c) => (c.id === id ? previousClaim : c)),
          error: error instanceof Error ? error.message : 'Failed to update claim',
        }));
      }
      throw error;
    }
  },

  deleteClaim: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      await api.delete(`/api/claims/${id}`);
      set((state) => ({
        claims: state.claims.filter((c) => c.id !== id),
        currentClaim: state.currentClaim?.id === id ? null : state.currentClaim,
        isLoading: false,
      }));
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to delete claim',
        isLoading: false 
      });
      throw error;
    }
  },

  fetchDocuments: async (claimId: string) => {
    try {
      const data = await api.get<{ documents: Document[] }>(`/api/claims/${claimId}/documents`);
      if (isStale(claimId)) return;
      set({ documents: data.documents });
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    }
  },

  uploadDocument: async (claimId: string, file: File, type: string) => {
    const document = await api.upload<Document>(
      `/api/claims/${claimId}/documents`,
      file,
      { type }
    );

    set((state) => ({
      documents: [...state.documents, document],
    }));
    
    // Start polling for this document's analysis to complete
    const { pollForDocumentAnalysis } = get();
    pollForDocumentAnalysis(claimId, document.id);
    
    return document;
  },
  
  // Poll for a specific document's AI analysis to complete
  pollForDocumentAnalysis: async (claimId: string, documentId: string) => {
    const maxAttempts = 30; // 30 attempts * 2 seconds = 60 seconds max
    const pollInterval = 2000; // 2 seconds
    const generation = pollGeneration;
    
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await new Promise(resolve => setTimeout(resolve, pollInterval));
      
      // Stop polling if the user navigated away or a different claim loaded
      if (generation !== pollGeneration || isStale(claimId)) return;
      
      try {
        const data = await api.get<{ documents: Document[] }>(`/api/claims/${claimId}/documents`);
        if (generation !== pollGeneration || isStale(claimId)) return;
        
        const doc = data.documents?.find((d: any) => d.id === documentId);
        
        if (doc?.aiProcessed) {
          // Update the documents in store
          set({ documents: data.documents });
          
          // Check if all documents are now processed
          const allProcessed = data.documents.every((d: any) => d.aiProcessed);
          
          if (allProcessed) {
            // Auto-recalculate evidence score
            const { analyseEvidence, fetchClaim } = get();
            try {
              await analyseEvidence(claimId);
              await fetchClaim(claimId);
            } catch {
              // Non-critical error, continue
            }
          }
          
          return; // Done polling
        }
      } catch {
        // Continue polling on error
      }
    }
  },

  deleteDocument: async (documentId: string, options?: { reason?: string; category?: string }) => {
    // Build URL with optional query params for over-limit tracking
    let url = `/api/documents/${documentId}`;
    if (options?.reason) {
      const params = new URLSearchParams();
      params.set('reason', options.reason);
      if (options.category) params.set('category', options.category);
      url += `?${params.toString()}`;
    }
    
    const data = await api.delete<{ claimId?: string }>(url);
    const claimId = data.claimId;
    
    set((state) => ({
      documents: state.documents.filter((d) => d.id !== documentId),
    }));

    // Refetch claim after deletion to get updated evidence score
    // The backend triggers re-analysis, so poll for updates
    if (claimId) {
      const generation = pollGeneration;
      const { fetchClaim, currentClaim } = get();
      const originalScore = currentClaim?.evidenceScore;
      
      // Poll for updated score (max 10 seconds)
      for (let attempt = 1; attempt <= 5; attempt++) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        if (generation !== pollGeneration || isStale(claimId)) return;
        await fetchClaim(claimId);
        const updatedClaim = get().currentClaim;
        
        // If score changed or we've waited long enough, stop polling
        if (updatedClaim?.evidenceScore !== originalScore || attempt >= 5) {
          break;
        }
      }
    }
  },

  analyseEvidence: async (claimId: string) => {
    set({ isLoading: true, error: null });
    try {
      const data = await api.post<any>(`/api/claims/${claimId}/analyse`);
      
      // Update the current claim with analysis results
      set((state) => ({
        currentClaim: state.currentClaim ? {
          ...state.currentClaim,
          evidenceScore: data.evidenceScore,
          evidenceAnalysis: {
            ...data.analysis,
            aiReasoning: data.aiReasoning,
          },
          status: data.evidenceScore >= 70 ? 'evidence_complete' : 'draft',
        } : null,
        isLoading: false,
      }));
      
      return data;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to analyse evidence';
      console.error('[Store] Analysis failed:', errorMessage);
      set({ 
        error: errorMessage,
        isLoading: false 
      });
      throw error;
    }
  },

  // Prior Reports
  fetchPriorReports: async (claimId: string) => {
    try {
      const data = await api.get<{ priorReports: (PriorReport & { daysBefore?: number })[] }>(
        `/api/claims/${claimId}/prior-reports`
      );
      if (isStale(claimId)) return;
      set({ priorReports: data.priorReports });
    } catch (error) {
      console.error('Failed to fetch prior reports:', error);
    }
  },

  searchPriorReports: async (claimId: string) => {
    set({ isLoading: true, error: null });
    try {
      const data = await api.post<PriorReportsSearchResult>(
        `/api/claims/${claimId}/search-prior-reports`
      );
      
      // Update prior reports in state
      set({ 
        priorReports: data.reports,
        isLoading: false,
      });
      
      // Update claim's hasPriorReports flag
      set((state) => ({
        currentClaim: state.currentClaim ? {
          ...state.currentClaim,
          hasPriorReports: data.found > 0,
        } : null,
      }));
      
      return data;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to search prior reports';
      console.error('[Store] Prior reports search failed:', errorMessage);
      set({ error: errorMessage, isLoading: false });
      throw error;
    }
  },

  confirmPriorReport: async (claimId: string, reportId: string) => {
    try {
      // Response contains updated report data
      await api.post(`/api/claims/${claimId}/prior-reports/${reportId}/confirm`);
      
      set((state) => ({
        priorReports: state.priorReports.map((r) =>
          r.id === reportId ? { ...r, userConfirmedMatch: true } : r
        ),
      }));
      
      // Backend triggers evidence re-analysis; poll for updated score with retries.
      // The poll is cancelled when the user navigates away or another claim loads.
      const generation = pollGeneration;
      
      const pollForUpdate = async (attempt: number = 1, maxAttempts: number = 5) => {
        if (generation !== pollGeneration || isStale(claimId)) return;
        try {
          const updatedClaim = await api.get<Claim>(`/api/claims/${claimId}`);
          if (generation !== pollGeneration || isStale(claimId)) return;
          const currentClaim = get().currentClaim;
          
          // Check if evidence score has changed or we've reached max attempts
          if (updatedClaim.evidenceScore !== currentClaim?.evidenceScore || attempt >= maxAttempts) {
            set({ currentClaim: updatedClaim });
          } else if (attempt < maxAttempts) {
            // Score hasn't changed yet, try again
            setTimeout(() => pollForUpdate(attempt + 1, maxAttempts), 2000);
          }
        } catch {
          // Non-critical poll error
        }
      };
      
      // Start polling after 2 seconds
      setTimeout(() => pollForUpdate(1, 5), 2000);
    } catch (error) {
      console.error('Failed to confirm prior report:', error);
      throw error;
    }
  },

  removePriorReport: async (claimId: string, reportId: string) => {
    try {
      await api.delete(`/api/claims/${claimId}/prior-reports/${reportId}`);
      
      set((state) => ({
        priorReports: state.priorReports.filter((r) => r.id !== reportId),
      }));
      
      // Update claim's hasPriorReports flag
      set((state) => {
        const remainingReports = state.priorReports.length;
        return {
          currentClaim: state.currentClaim ? {
            ...state.currentClaim,
            hasPriorReports: remainingReports > 0,
          } : null,
        };
      });
    } catch (error) {
      console.error('Failed to remove prior report:', error);
      throw error;
    }
  },

  identifyAuthority: async (claimId: string) => {
    try {
      const data = await api.post<AuthorityResult>(`/api/claims/${claimId}/identify-authority`);
      
      // Refresh the claim to get the updated authority link
      await get().fetchClaim(claimId);
      
      return data;
    } catch (error) {
      console.error('Failed to identify authority:', error);
      throw error;
    }
  },

  fetchAuthority: async (claimId: string) => {
    try {
      return await api.get<AuthorityResult>(`/api/claims/${claimId}/authority`);
    } catch (error) {
      console.error('Failed to fetch authority:', error);
      throw error;
    }
  },

  fetchPacks: async (claimId: string) => {
    try {
      const data = await api.get<{ packs: ClaimPack[] }>(`/api/claims/${claimId}/packs`);
      if (isStale(claimId)) return;
      set({ packs: data.packs });
    } catch (error) {
      console.error('Failed to fetch packs:', error);
    }
  },

  setPackGenerating: (generating: boolean) => {
    set({ isPackGenerating: generating });
  },

  getRealtimeToken: async (claimId: string, runIds?: string[]) => {
    try {
      return await api.post<{ publicToken: string; claimTag: string; expiresIn: number }>(
        `/api/claims/${claimId}/realtime-token`,
        { runIds }
      );
    } catch (error) {
      console.error('Failed to get realtime token:', error);
      throw error;
    }
  },

  clearCurrentClaim: () => {
    activeClaimId = null;
    pollGeneration++;
    set({
      currentClaim: null,
      documents: [],
      priorReports: [],
      packs: [],
      error: null,
      isLoading: false,
      isPackGenerating: false,
    });
  },
}));
